import CompanyModel, { CompanyLogo } from './CompanyModel';
import CouponModel from './CouponModel';
import { FacebookErrorModel } from './FacebookErrorModel';
import PromotionModel from './PromotionModel';
import { SubscriptionModel } from './SubscriptionModel';

export enum NotificationKind {
  InactiveBot = 'inactive_bot',
}

type Notification = {
  kind: NotificationKind;
};

type TokenStatus = {
  status: string;
  details: string;
};

export enum Trend {
  Up = 'up',
  Down = 'down',
  Same = 'same',
}

export type OutgoingMessagesStatistic = {
  pageMessagingCount: number;
  prevPageMessagingCount: number;
  subscriptionMessagingCount: number;
  prevSubscriptionMessagingCount?: number;
  pageLockedAt: string | null;
  limitReachedAt: string | null;
};

export type EnhancedOutgoingMessagesStatistic = {
  pageSpecificCount: number;
  count: number;
  limitReachedAt: string;
  pageLockedAt: string;
  utilizationPercentage: number;
  outgoingMessageLimit: number;
  cappedCount: number;
  messagesRemaining: number;
};

export type CurrentOutgoingMessagesStatistic = {
  id: number;
  createdAt: string;
  count: string | null;
  limitReachedAt: string | null;
  messagesNotProcessedCount: number;
  softLimitReached: boolean;
  hardLimitReached: boolean;
  twilioLimitReachedAt: string;
  twilioSoftDailyLimitReachedAt: string;
  twilioSoftLimitReachedAt: string;
  leadsCaptured: number | null;
};

export type InstagramAccountData = {
  remoteId: string;
  username: string;
  name: string;
  profilePic: string | null;
  followersCount: number | null;
  followedByCount?: number | null;
};

export type LockStrategy = 'fb_page_level' | 'twilio_level';

export type MMCategory =
  | 'travel_transportation'
  | 'topic_sports_recreation'
  | 'topic_shopping_retail'
  | 'topic_real_estate'
  | 'topic_automotive'
  | 'topic_arts_entertainment'
  | 'spa_beauty_personal_care'
  | 'science_engineering'
  | 'public_services_government'
  | 'non_profit'
  | 'ngo'
  | 'medical_health'
  | 'media_news_company'
  | 'local_services'
  | 'legal'
  | 'hotel_lodging'
  | 'food_beverage'
  | 'finance'
  | 'education_company'
  | 'commercial_industrial'
  | 'agriculture'
  | 'advertising_marketing'
  | 'community_organization'
  | 'media'
  | 'interest'
  | 'public_figure'
  | 'other'
  | 'non_business_places';

export type Settings = null | {
  xrayLimit?: number | null;
};

export enum PageSourceType {
  facebook = 'fb_only',
  freeLeadGenerator = 'free_lead_generator',
  automatedResponseTool = 'automated_response_tool',
  websiteOnly = 'website_only',
  freeLeadGeneratorConverted = 'free_lead_generator_converted',
  automatedResponseToolConverted = 'automated_response_tool_converted',
  websiteOnlyConverted = 'website_only_converted',
  instachampDemo = 'instachamp_demo',
}

export default interface ConnectedPageModel {
  id: number;
  name: string | null; // Note: we have actual BE data containing null here
  // TODO: Pretty sure this field is never returned from our BE but need to double check
  botId: number;
  businessAssetId?: number;
  businessWebsite?: string;
  businessLogo?: CompanyLogo;
  business?: boolean;
  remoteId: string;
  activeBotId: number;
  firstBotId: number;
  newContactsThisWeek: number;
  newSessionsThisWeek: number;
  sessionsTrend: Trend;
  sessionsTrendPercentage: number;
  squarePicture?: string;
  totalContacts: number;
  followUpConversationsCount: number;
  subscription?: SubscriptionModel;
  pastSubscriptionIds?: number[];
  createdAt: string;
  facebookErrors: FacebookErrorModel[];
  partners?: CompanyModel[];
  coupon?: CouponModel;
  redemption?: PromotionModel;
  pageRedemptions?: PromotionModel[];
  downgradePending?: boolean;
  tokenStatus: TokenStatus;
  companyId: number;
  contactsTrend: Trend;
  contactsTrendPercentage: number;
  totalContactsTrend: Trend;
  totalContactsTrendPercentage: number;
  banned: boolean;
  notificationsEnabled: boolean;
  contactsCount?: number;
  mailingSettings?: {
    leadsEmail: boolean;
  };
  notifications: Notification[];
  outgoingMessageUsage?: OutgoingMessagesStatistic | null;
  subscriptions?: SubscriptionModel[];
  outgoingMessagesLimit?: number;
  overagesEnabled?: boolean;
  subscriptionMessagingStatus: string;
  coverPhoto?: string;
  mmCategory: MMCategory;
  hasCtmAds: boolean;
  hasSponsoredAds: boolean;
  twilioAccessible: boolean;
  twilioCampaignCreated: boolean;
  twilioDailyLocked: boolean;
  twilioDailyLockedTill: string;
  currentOutgoingMessagesStatistic: CurrentOutgoingMessagesStatistic;
  prevOutgoingMessagesStatistic?: CurrentOutgoingMessagesStatistic;
  connectedInstagramAccounts?: string[];
  lockStrategy: LockStrategy;
  softLimitReached: boolean;
  hardLimitReached: boolean;
  smsActivated: boolean;
  smsNumberActivated: boolean;
  emailActivated: boolean;
  webChatActivated: boolean;
  zapierActivated: boolean;
  commentGuardActivated: boolean;
  blasterActivated: boolean;
  notificationWidgetActivated: boolean;
  withActiveWebChatPromoter: boolean;
  instagramActivated: boolean;
  realFacebookPage?: boolean;
  euLimitsAt: null | string;
  availableInstagramAccountsData?: InstagramAccountData;
  settings: Settings;
  src?: PageSourceType;
  standaloneToken?: string;
  facebookActive?: boolean;
  instagramActive?: boolean;
  free: boolean;
  onceSuccessfullyLoadedGlobalJs?: boolean;
  getemailsActivated?: boolean;
  xrayLockedAt: string;
}
